/**
 * NsectionAppendixPage
 * 附錄（A6.4-FORM-METH-002 Appendix）
 * Card + DataTable CRUD + Modal
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionAppendixService, { NsectionAppendixDetail, AppendixItem } from '../services/nsectionAppendixService';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const NsectionAppendixPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionAppendixDetail | null>(null);
  const [appendices, setAppendices] = useState<AppendixItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalTitle, setModalTitle] = useState('');
  const [modalContent, setModalContent] = useState('');

  useEffect(() => { if (!hasInitialized.current && !permissionLoading) { validateSession('NsectionAppendixPage'); hasInitialized.current = true; } }, [permissionLoading]);

  useEffect(() => {
    if (!selectedProposalId) { setData(null); setAppendices([]); return; }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionAppendixService.getByProposal(selectedProposalId);
        if (r) { setData(r); setAppendices(r.appendices || []); }
        else { setData(null); setAppendices([]); }
      } catch (err: any) { if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed')); }
      finally { setLoading(false); }
    };
    load();
  }, [selectedProposalId, isZh]);

  const openModal = (index?: number) => {
    if (index !== undefined) {
      setEditingIndex(index);
      setModalTitle(appendices[index].title);
      setModalContent(appendices[index].content);
    } else {
      setEditingIndex(null);
      setModalTitle('');
      setModalContent('');
    }
    setShowModal(true);
  };

  const handleModalConfirm = () => {
    if (!modalTitle.trim()) { toast.warning(isZh ? '請輸入附錄名稱' : 'Please enter appendix title'); return; }
    const updated = [...appendices];
    const item: AppendixItem = { title: modalTitle.trim(), content: modalContent };
    if (editingIndex !== null) updated[editingIndex] = item; else updated.push(item);
    setAppendices(updated);
    setShowModal(false);
  };

  const handleDelete = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定要刪除此附錄嗎？' : 'Delete this appendix?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setAppendices(appendices.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const r = await nsectionAppendixService.update(selectedProposalId, { appendices });
      setData(r); toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Failed')); }
    finally { setLoading(false); }
  };

  // 內容摘要（去 HTML 取前 100 字）
  const getSummary = (html: string, maxLen = 100): string => {
    const text = html.replace(/<[^>]+>/g, '').trim();
    return text.length > maxLen ? text.substring(0, maxLen) + '...' : text || '—';
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_appendix', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canUpdate = hasPermission('nsection_appendix', 'update');

  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_appendix" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>{isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}</div>
      ) : loading && !data ? (
        <div className="loading">{t('common.loading')}</div>
      ) : (
        <div style={{ width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, paddingBottom: 12, borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#333' }}>{isZh ? '附錄清單' : 'Appendices'}</div>
            {canUpdate && <button className="btn-primary" onClick={() => openModal()} style={{ fontSize: 13, padding: '6px 16px' }}>{t('common.create')}</button>}
          </div>

          {appendices.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#999', fontSize: 14 }}>{isZh ? '尚無附錄（選填）' : 'No appendices (optional).'}</div>
          ) : (
            <div className="data-table-container"><table className="data-table"><thead className="table-header-dark-green"><tr>
              <th style={{ width: '10%' }}>#</th>
              <th style={{ width: '25%' }}>{isZh ? '附錄名稱' : 'Title'}</th>
              <th style={{ width: '50%' }}>{isZh ? '內容摘要' : 'Summary'}</th>
              <th style={{ width: '15%' }}>{t('common.actions')}</th>
            </tr></thead><tbody>
              {appendices.map((item, idx) => (
                <tr key={idx}>
                  <td style={{ fontWeight: 600 }}>Appendix {idx + 1}</td>
                  <td style={{ fontWeight: 600 }}>{item.title}</td>
                  <td style={{ fontSize: 13, color: '#666' }} dangerouslySetInnerHTML={{ __html: item.content || '—' }} />
                  <td className="actions">{canUpdate && <>
                    <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: 8 }}>{t('common.edit')}</button>
                    <button className="btn-delete" onClick={() => handleDelete(idx)}>{t('common.delete')}</button>
                  </>}</td>
                </tr>
              ))}
            </tbody></table></div>
          )}

          {canUpdate && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
              <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>{loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}</button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 800 }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯附錄' : 'Edit Appendix') : (isZh ? '新增附錄' : 'Add Appendix')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>{isZh ? '附錄名稱' : 'Title'} *</label>
                <input value={modalTitle} onChange={e => setModalTitle(e.target.value)}
                  placeholder={isZh ? '例：Appendix 1. Default emission factors' : 'e.g. Appendix 1. Default emission factors'}
                  style={{ width: '100%', padding: '8px 12px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 6, fontWeight: 600, fontSize: 14 }}>{isZh ? '附錄內容' : 'Content'}</label>
                <RichTextEditor value={modalContent} onChange={setModalContent} placeholder={isZh ? '請輸入附錄內容...' : 'Enter appendix content...'} minHeight="250px" />
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              <button className="btn-primary" onClick={handleModalConfirm}>{t('common.save')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionAppendixPage;
